import { Injectable, inject } from '@angular/core';
import { NativeService } from '@shared/services/native.service';
import { LoggerService } from '@shared/services/logger.service';
import { distinctUntilChanged, filter, map, pairwise } from 'rxjs/operators';
import { ProgressLineRepository } from '../state/progress-line/progress-line.repository';

@Injectable({
  providedIn: 'root',
})
export class AppReviewService {
  private nativeService = inject(NativeService);

  readonly inAppReviewMinMilestoneReached = 2;

  constructor() {
    const progressLineRepo = inject(ProgressLineRepository);
    const logger = inject(LoggerService);

    progressLineRepo.casino$
      .pipe(
        map((c) => ({
          order: c?.order as number | null,
          milestone: c?.lastStepRedeemed as number | null,
        })),
        filter((x): x is { order: number; milestone: number } => x.order !== null && x.milestone !== null),
        distinctUntilChanged((a, b) => a.order === b.order && a.milestone === b.milestone),
        pairwise(),
        filter(([prev, curr]) => this.shouldShowInAppReviewAfterLevelUp(prev, curr))
      )
      .subscribe(() => {
        this.startInAppReview().catch(logger.error);
      });
  }

  /**
   * Opens the store to download or update the game. Is not intended for Desktop devices.
   */
  openStore() {
    return this.delegateStartAppReview(false, false, false);
  }

  /**
   * Open the review app prioritizing the In-App and if is not possible the Out-App.
   */
  startReview() {
    return this.delegateStartAppReview(true, true, true);
  }

  /**
   * Tries to show the In-app review without going outside the app.
   * This is not always available the platform is going to decide de behavior.
   */
  startInAppReview() {
    return this.delegateStartAppReview(true, false, false);
  }

  private delegateStartAppReview(inAppReview = true, fallBackToOutApp = true, writeReview = false): Promise<void> {
    if (!this.nativeService.isNative()) {
      return fallBackToOutApp ? this.openDesktopReviewPage() : Promise.resolve();
    }

    return this.nativeService.startAppReview(inAppReview, fallBackToOutApp, writeReview);
  }

  private shouldShowInAppReviewAfterLevelUp(
    prev: { order: number; milestone: number },
    curr: { order: number; milestone: number }
  ): boolean {
    // Order just reached a new casino and milestone already satisfied
    if (curr.order > prev.order && curr.milestone > this.inAppReviewMinMilestoneReached) return true;

    // Milestone just crossed the threshold while already at a multiple of 5
    if (
      curr.order === prev.order &&
      prev.milestone <= this.inAppReviewMinMilestoneReached &&
      curr.milestone > this.inAppReviewMinMilestoneReached
    )
      return true;

    return false;
  }

  private openDesktopReviewPage() {
    window.open('https://www.facebook.com/slotcom/reviews', '_blank');
    return Promise.resolve();
  }
}
